import React, { useState } from 'react';
import axios from 'axios';
import { Button, Form, Header, Icon, Input, Loader, Message, Segment, Table } from 'semantic-ui-react';
import ExcelSheet from 'react-data-export/dist/ExcelPlugin/elements/ExcelSheet';
import ReportExcelFile from './ReportExcelFile';
import { requiredFilterSectionStyle, shouldShowOrgUnitFilter } from './reportUiHelpers';

const title = 'Broj upisanih i diplomiranih studenata po ciklusima i studijskim programima';
// A cell following a row-spanning cycle can be the first DOM cell, but is not the first column.
const columnDividerStyle = { borderLeft: '1px solid rgba(34, 36, 38, 0.15)' };

export default function StudentAccreditationReport({ academicYears, organizationalUnits, closeReport }) {
    const [academicYearId, setAcademicYearId] = useState(null);
    const [organizationalUnitId, setOrganizationalUnitId] = useState(null);
    const [rows, setRows] = useState(null);
    const [reportYear, setReportYear] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [search, setSearch] = useState('');
    const orgFilter = shouldShowOrgUnitFilter(localStorage.getItem('activeRole'));
    const invalidate = () => { setRows(null); setError(false); setSearch(''); };
    const fetchReport = async () => {
        setLoading(true);
        setError(false);
        setRows(null);
        try {
            const response = await axios.get('reports/student-accreditation', {
                params: { academicYearId, ...(orgFilter ? { organizationalUnitId } : {}) }
            });
            setRows(response.data);
            const year = academicYears.find(item => item.value === academicYearId);
            setReportYear(year ? year.text : '');
        } catch (err) {
            setError(true);
        } finally {
            setLoading(false);
        }
    };
    const filtered = (rows || []).filter(row =>
        `${row.studyCycleName} ${row.studyProgramBasicName}`.toLocaleLowerCase().includes(search.toLocaleLowerCase()));
    const groups = [];
    filtered.forEach(row => {
        let group = groups.find(item => item.id === row.studyCycleId);
        if (!group) {
            group = { id: row.studyCycleId, name: row.studyCycleName, rows: [] };
            groups.push(group);
        }
        group.rows.push(row);
    });
    const sum = (items, key) => items.reduce((total, row) => total + row[key], 0);
    const exportRows = [];
    groups.forEach(group => {
        group.rows.forEach(row => exportRows.push([group.name, row.studyProgramBasicName, row.enrolledStudents, row.graduatedStudents]));
        exportRows.push([group.name, 'Ukupno za ciklus', sum(group.rows, 'enrolledStudents'), sum(group.rows, 'graduatedStudents')]);
    });
    if (filtered.length) exportRows.push(['Ukupno', '', sum(filtered, 'enrolledStudents'), sum(filtered, 'graduatedStudents')]);
    const enrolledLabel = `Broj upisanih studenata u zimski semestar ${reportYear}`;
    const graduatedLabel = `Broj diplomiranih studenata u akademskoj ${reportYear}`;
    return (
        <div>
            <Form>
                <Segment style={requiredFilterSectionStyle}>
                    <Header as="h4" color="blue">Obavezni parametri</Header>
                    <Form.Group widths={2}>
                        <Form.Dropdown
                            label="Akademska godina"
                            placeholder="Odaberite akademsku godinu"
                            selection
                            search
                            fluid
                            options={academicYears}
                            value={academicYearId}
                            onChange={(e, { value }) => { setAcademicYearId(value); invalidate(); }}
                        />
                        {orgFilter && <Form.Dropdown
                            label="Organizaciona jedinica"
                            placeholder="Odaberite organizacionu jedinicu"
                            selection
                            search
                            fluid
                            options={organizationalUnits}
                            value={organizationalUnitId}
                            onChange={(e, { value }) => { setOrganizationalUnitId(value); invalidate(); }}
                        />}
                    </Form.Group>
                </Segment>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button type="button" color="blue" onClick={fetchReport} disabled={loading || !academicYearId || (orgFilter && !organizationalUnitId)}>Prikaži izvještaj</Button>
                    <Button type="button" basic color="blue" onClick={closeReport}>Odustani</Button>
                </div>
            </Form>
            {loading && <Loader active inline="centered" />}
            {error && <Message negative>Izvještaj nije moguće učitati. Provjerite podatke i datume semestara za odabranu akademsku godinu.</Message>}
            {rows && !rows.length && <Message>Nema podataka za odabrane parametre.</Message>}
            {rows && rows.length > 0 && <Table celled compact>
                <Table.Header>
                    <Table.Row><Table.HeaderCell colSpan={4}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                            <Input
                                icon="search"
                                placeholder="Pretraga"
                                value={search}
                                style={{ flex: '1 1 240px' }}
                                onChange={(e, { value }) => setSearch(value)}
                            />
                            <div style={{ flex: '0 0 210px' }}><ReportExcelFile
                                filename={title}
                                element={<Button basic color="green" fluid animated="vertical"><Button.Content visible>Excel</Button.Content><Button.Content hidden><Icon name="download" /></Button.Content></Button>}
                            >
                                <ExcelSheet
                                    name="Studenti"
                                    dataSet={[{
                                        columns: ['Ciklus', 'Studijski program', enrolledLabel, graduatedLabel].map(text => ({ title: text })),
                                        data: exportRows.map(row => row.map(value => ({ value })))
                                    }]}
                                />
                            </ReportExcelFile></div>
                        </div>
                    </Table.HeaderCell></Table.Row>
                    <Table.Row><Table.HeaderCell>Ciklus</Table.HeaderCell><Table.HeaderCell style={columnDividerStyle}>Studijski program</Table.HeaderCell>
                        <Table.HeaderCell style={columnDividerStyle} textAlign="center">{enrolledLabel}</Table.HeaderCell><Table.HeaderCell style={columnDividerStyle} textAlign="center">{graduatedLabel}</Table.HeaderCell></Table.Row>
                </Table.Header>
                <Table.Body>
                    {groups.map(group => (<React.Fragment key={group.id}>
                        {group.rows.map((row, index) => (<Table.Row key={row.studyProgramBasicId}>
                            {index === 0 && <Table.Cell rowSpan={group.rows.length} verticalAlign="middle">{group.name}</Table.Cell>}
                            <Table.Cell style={columnDividerStyle}>{row.studyProgramBasicName}</Table.Cell><Table.Cell style={columnDividerStyle} textAlign="center">{row.enrolledStudents}</Table.Cell><Table.Cell style={columnDividerStyle} textAlign="center">{row.graduatedStudents}</Table.Cell>
                        </Table.Row>))}
                        <Table.Row active><Table.Cell colSpan={2}><strong>Ukupno — {group.name}</strong></Table.Cell>
                            <Table.Cell style={columnDividerStyle} textAlign="center"><strong>{sum(group.rows, 'enrolledStudents')}</strong></Table.Cell><Table.Cell style={columnDividerStyle} textAlign="center"><strong>{sum(group.rows, 'graduatedStudents')}</strong></Table.Cell></Table.Row>
                    </React.Fragment>))}
                </Table.Body>
                <Table.Footer><Table.Row><Table.HeaderCell colSpan={2}>Ukupno{search ? ' za prikazane programe' : ''}</Table.HeaderCell>
                    <Table.HeaderCell style={columnDividerStyle} textAlign="center">{sum(filtered, 'enrolledStudents')}</Table.HeaderCell><Table.HeaderCell style={columnDividerStyle} textAlign="center">{sum(filtered, 'graduatedStudents')}</Table.HeaderCell></Table.Row></Table.Footer>
            </Table>}
        </div>
    );
}



// WEBPACK FOOTER //
// ./src/views/StudentService/StudentsReports/StudentAccreditationReport.js